import { useWallet } from '@solana/wallet-adapter-react';
import { WalletName } from '@solana/wallet-adapter-base';
import React, { FC, MouseEvent, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Collapse } from './Collapse';
import { useWalletModal } from './useWalletModal';
import { WalletListItem } from './WalletListItem';

export interface WalletModalProps {
    className?: string;
    logo?: ReactNode;
    featuredWallets?: number;
    container?: string;
}

export const WalletModal: FC<WalletModalProps> = ({
    className = '',
    logo,
    featuredWallets = 3,
    container = 'body',
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const { wallets, select } = useWallet();
    const { setVisible } = useWalletModal();
    const [expanded, setExpanded] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [portal, setPortal] = useState<Element | null>(null);

    const [featured, more] = useMemo(
        () => [wallets.slice(0, featuredWallets), wallets.slice(featuredWallets)],
        [wallets, featuredWallets]
    );

    const hideModal = useCallback(() => {
        setFadeIn(false);
        setTimeout(() => setVisible(false), 150);
    }, [setFadeIn, setVisible]);

    const handleClose = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            hideModal();
        },
        [hideModal]
    );

    const handleWalletClick = useCallback(
        (event: MouseEvent, walletName: WalletName) => {
            select(walletName);
            handleClose(event);
        },
        [select, handleClose]
    );

    // update user login start
    const handleClickLoginUser = useCallback((event: MouseEvent) => {
        if (!(window.location.pathname.includes('login') || window.location.pathname.includes('register'))) {
            window.self.location.href = window.location.origin + '/login?redirect=' + (window.location.pathname + window.location.hash);
        }
        handleClose(event);
    }, [select, handleClose]);
    // update user login start

    const handleCollapseClick = useCallback(() => setExpanded(!expanded), [setExpanded, expanded]);

    const handleTabKey = useCallback(
        (event: KeyboardEvent) => {
            const node = ref.current;
            if (!node) return;

            // here we query all focusable elements
            const focusableElements = node.querySelectorAll('button');
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey) {
                // if going backward by pressing tab and firstElement is active, shift focus to last focusable element
                if (document.activeElement === firstElement) {
                    lastElement.focus();
                    event.preventDefault();
                }
            } else {
                // if going forward by pressing tab and lastElement is active, shift focus to first focusable element
                if (document.activeElement === lastElement) {
                    firstElement.focus();
                    event.preventDefault();
                }
            }
        },
        [ref]
    );

    useLayoutEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                hideModal();
            } else if (event.key === 'Tab') {
                handleTabKey(event);
            }
        };

        // Get original overflow
        const { overflow } = window.getComputedStyle(document.body);
        // Hack to enable fade in animation after mount
        setTimeout(() => setFadeIn(true), 0);
        // Prevent scrolling on mount
        document.body.style.overflow = 'hidden';
        // Listen for keydown events
        window.addEventListener('keydown', handleKeyDown, false);

        return () => {
            // Re-enable scrolling when component unmounts
            document.body.style.overflow = overflow;
            window.removeEventListener('keydown', handleKeyDown, false);
        };
    }, [hideModal, handleTabKey]);

    useLayoutEffect(() => setPortal(document.querySelector(container)), [setPortal, container]);

    const isLoginUser = !!window.localStorage.getItem('userStatus');
    return (
        portal &&
        createPortal(
            <div
                aria-labelledby="wallet-adapter-modal-title"
                aria-modal="true"
                className={`wallet-adapter-modal ${fadeIn && 'wallet-adapter-modal-fade-in'} ${className}`}
                ref={ref}
                role="dialog"
            >
                <div className="wallet-adapter-modal-container">
                    <div className={`wallet-adapter-modal-wrapper ${!logo && 'wallet-adapter-modal-wrapper-no-logo'}`}>
                        {logo && (
                            <div className="wallet-adapter-modal-logo-wrapper">
                                {typeof logo === 'string' ? (
                                    <img alt="logo" className="wallet-adapter-modal-logo" src={logo} />
                                ) : (
                                    logo
                                )}
                            </div>
                        )}
                        {/*update user login start*/}
                        {
                            isLoginUser && <h2 className="wallet-adapter-modal-title" id="wallet-adapter-modal-title" style={{
                                fontSize: "54px",
                                fontFamily: "Teko-Bold, Teko",
                                fontWeight: "bold",
                                color: "#FFFFFF",
                                lineHeight: "64px",
                                textShadow: "0px 0px 20px rgba(0, 255, 255, 0.8)",
                            }}>
                            Choose Solana Wallet
                          </h2>
                        }
                        {
                            !isLoginUser && <h1 className="wallet-adapter-modal-title" id="wallet-adapter-modal-title">
                            Login
                          </h1>
                        }
                        {/*update user login end*/}
                        <button onClick={handleClose} className="wallet-adapter-modal-button-close">
                            <svg width="14" height="14">
                                <path d="M14 12.461 8.3 6.772l5.234-5.233L12.006 0 6.772 5.234 1.54 0 0 1.539l5.234 5.233L0 12.006l1.539 1.528L6.772 8.3l5.69 5.7L14 12.461z" />
                            </svg>
                        </button>
                        <ul className="wallet-adapter-modal-list">
                            {/*update user login start*/}
                            {
                                !isLoginUser && <li>
                                <button className="wallet-adapter-button " type="button" style={{ justifyContent: 'space-between', marginBottom: "2rem", textAlign: "left", height: "4rem" }}
                                        onClick={handleClickLoginUser}>
                                    <span style={{ fontSize: "1rem", lineHeight: "1.2rem" }}>
                                        <span style={{ display: "block" }}>MetaGaia Wallet</span>
                                        <span style={{ fontSize: "1rem", fontFamily: "Arial", fontWeight: "normal", display: "block" }} className="txt-block">Login with email & password</span>
                                    </span>
                                  <i className="wallet-adapter-button-end-icon">
                                    <img src="data:image/png;base64,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"
                                         alt="Sollet (Extension) icon" style={{ width: '2rem', height: '2rem' }}/>
                                  </i>
                                </button>
                              </li>
                            }
                            {
                                isLoginUser && featured.map((wallet) => (
                                    ["Phantom", "Solflare"].includes(wallet.adapter.name) && <WalletListItem
                                    key={wallet.adapter.name}
                                    handleClick={(event) => handleWalletClick(event, wallet.adapter.name)}
                                    wallet={wallet.adapter}
                                  />
                                ))
                            }
                            {
                                !isLoginUser && featured.map((wallet) => (
                                    <WalletListItem
                                        key={wallet.adapter.name}
                                        handleClick={(event) => handleWalletClick(event, wallet.adapter.name)}
                                        wallet={wallet.adapter}
                                    />
                                ))
                            }
                            {/*update user login end*/}
                        </ul>

                        {more.length ? (
                            <>
                                <Collapse expanded={expanded} id="wallet-adapter-modal-collapse">
                                    <ul className="wallet-adapter-modal-list">
                                        {more.map((wallet) => (
                                            <WalletListItem
                                                key={wallet.adapter.name}
                                                handleClick={(event) => handleWalletClick(event, wallet.adapter.name)}
                                                tabIndex={expanded ? 0 : -1}
                                                wallet={wallet.adapter}
                                            />
                                        ))}
                                    </ul>
                                </Collapse>
                                <Button
                                    aria-controls="wallet-adapter-modal-collapse"
                                    aria-expanded={expanded}
                                    className={`wallet-adapter-modal-collapse-button ${expanded && 'wallet-adapter-modal-collapse-button-active'
                                        }`}
                                    endIcon={
                                        <svg width="11" height="6" xmlns="http://www.w3.org/2000/svg">
                                            <path d="m5.938 5.73 4.28-4.126a.915.915 0 0 0 0-1.322 1 1 0 0 0-1.371 0L5.253 3.736 1.659.272a1 1 0 0 0-1.371 0A.93.93 0 0 0 0 .932c0 .246.1.48.288.662l4.28 4.125a.99.99 0 0 0 1.37.01z" />
                                        </svg>
                                    }
                                    onClick={handleCollapseClick}
                                >
                                    {expanded ? 'Less' : 'More'} options
                                </Button>
                            </>
                        ) : null}
                    </div>
                </div>
                <div className="wallet-adapter-modal-overlay" onMouseDown={handleClose} />
            </div>,
            portal
        )
    );
};
